import React, { useCallback, useMemo, useRef } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { Box, List, ListItem, Typography, keyframes, useTheme } from '@mui/material';

import { ActivityType, ActorType } from 'src/generated/mloop-graphql';

import Link from 'src/components/Link';

import ActivityLog from 'src/entities/ActivityLog';
import { ActivityLogListItemProvider } from 'src/entities/ActivityLog/ActivityLogListItemContext';
import { AnsweredQuestionsProvider } from 'src/entities/ActivityLog/AnsweredQuestionsContext';
import { ActivityLogLike } from 'src/entities/ActivityLog/types';

import { ActivityLogOrder, useFilteredActivityLogs } from './useFilteredActivityLogs';

const shimmer = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

interface Props {
  onViewMoreClick: () => void;
  activityTypes?: ActivityType[];
  activityLogOrder?: ActivityLogOrder;
  isLoadingMore?: boolean;
  showViewMoreButton?: boolean;
  viewMorePlacement?: 'start' | 'end';
  emptyText?: string;
}
interface Fragments {
  activityLogs: ActivityLogLike[];
}

/**
 * Renders activity logs in caller-provided order and derives shared row context.
 */
const ActivityLogsList: FCWithFragments<Fragments, Props> = ({
  activityLogs,
  onViewMoreClick,
  activityLogOrder = 'NEWEST_FIRST',
  isLoadingMore = false,
  showViewMoreButton,
  viewMorePlacement = 'end',
  emptyText,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { filteredLogs, isShimmeringId } = useFilteredActivityLogs(activityLogs, activityLogOrder);

  // Compute which AGENT_QUESTION logs have been answered by later non-question activity.
  const answeredQuestionIds = useMemo(() => {
    const ids = new Set<string>();
    const logsNewestToOldest = activityLogOrder === 'NEWEST_FIRST' ? filteredLogs : [...filteredLogs].reverse();
    let sawNonQuestion = false;
    for (const log of logsNewestToOldest) {
      if (log.activityType === ActivityType.AgentQuestion) {
        if (sawNonQuestion) {
          ids.add(log.id);
        }
      } else {
        sawNonQuestion = true;
      }
    }
    return ids;
  }, [activityLogOrder, filteredLogs]);

  const firstInAgentRunIds = useMemo(() => {
    const ids = new Set<string>();

    filteredLogs.forEach((log, index) => {
      if (log.activityActorType !== ActorType.Agent) {
        return;
      }

      const previousLog = filteredLogs[index - 1];
      if (previousLog?.activityActorType !== ActorType.Agent) {
        ids.add(log.id);
      }
    });

    return ids;
  }, [filteredLogs]);

  // Track which IDs were present on the previous render so new ones can be animated
  const seenIdsRef = useRef<Set<string>>(new Set());
  const newIds = useMemo(() => {
    const incoming = new Set(filteredLogs.map((log) => log.id));
    const fresh = new Set([...incoming].filter((id) => !seenIdsRef.current.has(id)));
    // Only animate if this isn't the very first render (seenIdsRef is empty)
    const isFirstRender = seenIdsRef.current.size === 0;
    seenIdsRef.current = incoming;
    return isFirstRender ? new Set<string>() : fresh;
  }, [filteredLogs]);

  const renderItem = useCallback(
    (option: ActivityLogLike, index: number) => {
      const isShimmering = option?.id === isShimmeringId;

      return (
        <ListItem
          key={option?.id ?? index}
          disableGutters
          disablePadding={false}
          sx={{
            overflow: 'hidden',
            ...(newIds.has(option?.id) ? { animation: 'fadeSlideUp 0.35s ease-out both' } : {}),
            ...(isShimmering ? { animation: `${shimmer} 1.5s ease-in-out infinite` } : {}),
          }}
        >
          <ActivityLogListItemProvider
            value={{
              isLastActivityLog: index === filteredLogs.length - 1,
              isFirstInAgentRun: firstInAgentRunIds.has(option?.id),
            }}
          >
            <ActivityLog activityLog={option} />
            {/* This box renders the vertical line between activity logs */}
            {index < filteredLogs.length - 1 ? (
              <Box
                height="calc(100% - 28px)"
                border={`1px solid ${theme.palette.borderState.default}`}
                position="absolute"
                marginLeft="9px"
                marginTop="35px"
                borderRadius="1px"
              />
            ) : null}
          </ActivityLogListItemProvider>
        </ListItem>
      );
    },
    [filteredLogs.length, theme, newIds, isShimmeringId, firstInAgentRunIds]
  );

  const viewMoreItem =
    showViewMoreButton || isLoadingMore ? (
      <ListItem disablePadding disableGutters>
        {isLoadingMore ? (
          <Typography color="text.secondary" role="status">
            {t('hiring-dashboard-common:common.activityLogs.loadingMore')}
          </Typography>
        ) : (
          <>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link component="button" href="" onClick={onViewMoreClick} color="info">
              {t('hiring-dashboard-common:common.activityLogs.showMore')}
            </Link>
          </>
        )}
      </ListItem>
    ) : null;

  return filteredLogs.length ? (
    <AnsweredQuestionsProvider value={answeredQuestionIds}>
      <List>
        {viewMorePlacement === 'start' ? viewMoreItem : null}
        {filteredLogs.map((activityLog, index) => renderItem(activityLog, index))}
        {viewMorePlacement === 'end' ? viewMoreItem : null}
      </List>
    </AnsweredQuestionsProvider>
  ) : (
    <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={100}>
      <Typography color="text.secondary">
        {emptyText ?? t('hiring-dashboard-common:common.activityLogs.noActivity')}
      </Typography>
    </Box>
  );
};

ActivityLogsList.fragments = {
  activityLogs: gql`
    ${ActivityLog.fragments.activityLog}
    fragment ActivityLogsList_activityLog on ActivityLog {
      id
      activityType
      ...ActivityLog_activityLog
    }
  `,
};

export default ActivityLogsList;
